import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The Update Wallet Input used by the constructor of Update Wallet Message.  Either personId or email are required to identify the wallet.
 */
export interface UpdateWalletMessageInput {
  /**
   * Bambu person identifier
   */
  personId?: string;
  /**
   * email on the person.
   */
  email?: string;
  /**
   * message to write to the wallet card
   */
  message?: string;
}

/**
 * The UpdateWalletMessage response expected from the execution of the object within BambuClient execute.
 */
export interface UpdateWalletMessageResponse {
  /**
   * the message response from the request
   */
  message: string;
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
  /**
   * If the person already exists, the personId is returned.
   */
  successful: boolean;
}

/**
 * The Update Wallet message takes a personId or email address and a message and uses that information to identify an existing wallet card within the tenant.  Then the message field on the card is udpated.
 */
export class UpdateWalletMessage extends Executable<UpdateWalletMessageResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'patch';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the UpdateWalletMessageResponse
   * @returns UpdateWalletMessageResponse
   */
  public getResponse(apiResponse: any): UpdateWalletMessageResponse {
    return {
      successful: apiResponse && apiResponse.msg === 'Updated wallet',
      message: apiResponse.msg,
    };
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    return `${tenantId}/wallet-message`;
  }

  private _personId?: string;

  private _email?: string;

  private _message?: string;

  constructor(input?: UpdateWalletMessageInput) {
    super();
    if (input) {
      this._email = input.email;
      this._personId = input.personId;
      this._message = input.message;
    }
  }

  /**
   * Sets the personId on the person.
   * @param value <string> person id
   * @returns <UpdateWalletMessage>
   */
  public setPersonId(value: string): UpdateWalletMessage {
    this._personId = value;
    return this;
  }

  /**
   * Sets the email address on the person.
   * @param value <string> email
   * @returns <UpdateWalletMessage>
   */
  public setEmail(value: string): UpdateWalletMessage {
    this._email = value;
    return this;
  }

  /**
   * Sets the message on the wallet card
   * @param value <string> message to be updated.
   * @returns <UpdateWalletMessage>
   */
  public setMessage(value: string): UpdateWalletMessage {
    this._message = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {};
    if (this._email) {
      input.email = this._email;
    }
    if (this._personId) {
      input.personId = this._personId;
    }
    if (this._message) {
      input.message = this._message;
    }
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
